import { createClient } from '@supabase/supabase-js';
import { Database } from './src/types/supabase';
import { WaitlistStatus } from '@wammasys/shared';

/**
 * Environment variables must be set:
 * - SUPABASE_URL: The URL of your Supabase project
 * - SUPABASE_ANON_KEY: The anonymous key of your Supabase project
 */

// This is a placeholder file. The actual types will be generated using the Supabase CLI
// with the `npm run generate-types` command in the supabase package.

// For development, ensure that you have the Supabase CLI installed and the local Supabase
// running with `npm run start:local` before generating types.

// Load environment variables - using import.meta.env for Vite
const supabaseUrl = typeof import.meta !== 'undefined' ? import.meta.env.VITE_SUPABASE_URL : process.env.VITE_SUPABASE_URL;
const supabaseAnonKey = typeof import.meta !== 'undefined' ? import.meta.env.VITE_SUPABASE_ANON_KEY : process.env.VITE_SUPABASE_ANON_KEY;
const supabaseServiceRoleKey = typeof import.meta !== 'undefined' ? import.meta.env.VITE_SUPABASE_SERVICE_ROLE_KEY : process.env.VITE_SUPABASE_SERVICE_ROLE_KEY;

// Fallback values if env variables aren't available
const fallbackUrl = 'https://nqfnxhceemuubsxmjfvq.supabase.co';
const fallbackKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5xZm54aGNlZW11dWJzeG1qZnZxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDQyNTI1MjksImV4cCI6MjA1OTgyODUyOX0.RgFXwXwA1bUoC9BnUcBODdZkt36KeDzPt5pbnEKrtWs';

// Use environment variables or fallback to hardcoded values
const finalSupabaseUrl = supabaseUrl || fallbackUrl;
const finalSupabaseAnonKey = supabaseAnonKey || fallbackKey;

if (!finalSupabaseUrl || !finalSupabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

// Create Supabase client
export const supabase = createClient<Database>(finalSupabaseUrl, finalSupabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  },
});

// Create a service role client that bypasses RLS policies for admin operations
export const createServiceRoleClient = () => {
  if (!supabaseServiceRoleKey) {
    console.warn('No service role key provided. Using anonymous client instead.');
    return supabase;
  }
  
  return createClient<Database>(finalSupabaseUrl, supabaseServiceRoleKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
    global: {
      headers: {
        Authorization: `Bearer ${supabaseServiceRoleKey}`,
      },
    },
    db: {
      schema: 'public',
    },
  });
};

// Helper function to handle Supabase errors
export const handleSupabaseError = (error: any) => {
  console.error('Supabase error:', error);
  throw new Error(error.message || 'An error occurred with Supabase');
};

// Helper function to get user session
export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
};

// Helper function to get restaurant by owner
export const getRestaurantByOwner = async (ownerId: string) => {
  const { data, error } = await supabase
    .from('restaurants')
    .select('*')
    .eq('owner_id', ownerId)
    .single();
  
  if (error) throw error;
  return data;
};

// Alias for getRestaurantByOwner to use in dashboard
export const getUserRestaurant = async (userId: string) => {
  return supabase
    .from('restaurants')
    .select('*')
    .eq('owner_id', userId)
    .single();
};

/**
 * Create a Supabase client with custom credentials
 * @param customToken Custom Supabase JWT token
 * @returns Supabase client with custom auth
 */
export const createSupabaseClient = (customToken: string) => {
  return createClient<Database>(finalSupabaseUrl, finalSupabaseAnonKey, {
    auth: {
      persistSession: false,
      autoRefreshToken: false,
      detectSessionInUrl: false,
    },
    global: {
      headers: {
        Authorization: `Bearer ${customToken}`,
      },
    },
  });
};

// Auth helpers
export const signUp = async (email: string, password: string) => {
  return supabase.auth.signUp({ email, password });
};

export const signIn = async (email: string, password: string) => {
  return supabase.auth.signInWithPassword({ email, password });
};

export const signOut = async () => {
  return supabase.auth.signOut();
};

export const resetPassword = async (email: string) => {
  return supabase.auth.resetPasswordForEmail(email);
};

// User profile helpers
export const getUserProfile = async (userId: string) => {
  return supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();
};

export const updateUserProfile = async (userId: string, data: any) => {
  return supabase
    .from('users')
    .update(data)
    .eq('id', userId);
};

// Restaurant helpers
export const getRestaurant = async (restaurantId: string) => {
  // For testing with fake UUIDs
  if (restaurantId === '123e4567-e89b-12d3-a456-426614174000') {
    return {
      data: {
        id: restaurantId,
        name: 'Test Restaurant',
        address: '123 Test Street, Testville',
        logo_url: 'https://placehold.co/100x100?text=TR',
        currentWaitTime: '15-25 minutes',
        openTables: 3
      },
      error: null
    };
  }
  
  return supabase
    .from('restaurants')
    .select('*')
    .eq('id', restaurantId)
    .single();
};

export const updateRestaurant = async (restaurantId: string, data: any) => {
  return supabase
    .from('restaurants')
    .update(data)
    .eq('id', restaurantId);
};

// Waitlist helpers
export const getWaitlistEntries = async (restaurantId: string, status = WaitlistStatus.WAITING) => {
  // Create an array of possible status values to handle case differences
  const statusValues = [
    status, // The status as provided (uppercase from enum)
    status.toLowerCase(), // Lowercase version
    status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() // Title case version
  ];
  
  // Use in operator to match any of the possible status values
  return supabase
    .from('waitlist_entries')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .or(`status.eq.${statusValues[0]},status.eq.${statusValues[1]},status.eq.${statusValues[2]}`)
    .order('created_at', { ascending: false });
};

// Re-export waitlist functions from src/supabase.ts
export { 
  addToWaitlist,
  updateWaitlistEntry,
  seatCustomer,
  cancelWaitlistEntry,
  getWaitlistEntry,
  logWaitlistEvent,
  calculateWaitTime,
  getWaitlistHistory,
  getWaitlistSettings,
  updateWaitlistSettings
} from './src/supabase';

// Campaign helpers
export const getCampaigns = async (restaurantId: string) => {
  return supabase
    .from('campaigns')
    .select('*')
    .eq('restaurant_id', restaurantId)
    .order('created_at', { ascending: false });
};

export const createCampaign = async (data: any) => {
  return supabase
    .from('campaigns')
    .insert(data);
};

export const updateCampaign = async (campaignId: string, data: any) => {
  return supabase
    .from('campaigns')
    .update(data)
    .eq('id', campaignId);
};

// Storage helpers
export const uploadRestaurantLogo = async (file: File, userId: string) => {
  try {
    // Ensure the file is valid
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid file provided');
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${Date.now()}.${fileExt}`;
    
    // First, try to ensure the bucket exists
    try {
      // Check if the bucket exists
      const { data: buckets, error: bucketsError } = await supabase.storage
        .listBuckets();
        
      if (bucketsError) {
        console.error('Error listing buckets:', bucketsError);
      } else {
        const logoBucketExists = buckets.some(bucket => bucket.name === 'logos');
        
        // If bucket doesn't exist, try to create it
        if (!logoBucketExists && supabaseServiceRoleKey) {
          console.log('Logos bucket does not exist, attempting to create it');
          
          // Try to create the bucket using service role client
          const adminClient = createServiceRoleClient();
          const { error: createBucketError } = await adminClient.storage
            .createBucket('logos', {
              public: true, // Make files publicly accessible
              fileSizeLimit: 5242880, // 5MB limit
            });
            
          if (createBucketError) {
            console.error('Error creating logos bucket:', createBucketError);
          } else {
            console.log('Successfully created logos bucket');
          }
        }
      }
    } catch (bucketError) {
      console.error('Error handling bucket creation:', bucketError);
    }
    
    // Try uploading the file
    const { data, error } = await supabase.storage
      .from('logos')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: true
      });
      
    if (error) {
      // Check if the error is about the bucket not existing
      if (error.message && error.message.includes('bucket')) {
        console.error('Logos bucket does not exist. Please create it in the Supabase dashboard.');
        throw new Error('Storage bucket not configured. Please contact the administrator.');
      }
      throw error;
    }
    
    // Get the public URL
    const { data: publicUrlData } = supabase.storage
      .from('logos')
      .getPublicUrl(fileName);
      
    return publicUrlData.publicUrl;
  } catch (error: any) {
    console.error('Error uploading logo:', error);
    throw new Error(error.message || 'Failed to upload logo');
  }
};

export const uploadMarketingAsset = async (file: File, userId: string, campaignId: string) => {
  try {
    // Ensure the file is valid
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid file provided');
    }

    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${campaignId}/${Date.now()}.${fileExt}`;
    
    // First, try to ensure the bucket exists
    try {
      // Check if the bucket exists
      const { data: buckets, error: bucketsError } = await supabase.storage
        .listBuckets();
        
      if (bucketsError) {
        console.error('Error listing buckets:', bucketsError);
      } else {
        const marketingBucketExists = buckets.some(bucket => bucket.name === 'marketing');
        
        // If bucket doesn't exist, try to create it
        if (!marketingBucketExists && supabaseServiceRoleKey) {
          console.log('Marketing bucket does not exist, attempting to create it');
          
          // Try to create the bucket using service role client
          const adminClient = createServiceRoleClient();
          const { error: createBucketError } = await adminClient.storage
            .createBucket('marketing', {
              public: true, // Make files publicly accessible
              fileSizeLimit: 10485760, // 10MB limit
            });
            
          if (createBucketError) {
            console.error('Error creating marketing bucket:', createBucketError);
          } else {
            console.log('Successfully created marketing bucket');
          }
        }
      }
    } catch (bucketError) {
      console.error('Error handling bucket creation:', bucketError);
    }
    
    // Try uploading the file
    const { data, error } = await supabase.storage
      .from('marketing')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });
      
    if (error) {
      // Check if the error is about the bucket not existing
      if (error.message && error.message.includes('bucket')) {
        console.error('Marketing bucket does not exist. Please create it in the Supabase dashboard.');
        throw new Error('Storage bucket not configured. Please contact the administrator.');
      }
      throw error;
    }
    
    // Get the public URL
    const { data: publicUrlData } = supabase.storage
      .from('marketing')
      .getPublicUrl(fileName);
      
    return publicUrlData.publicUrl;
  } catch (error: any) {
    console.error('Error uploading marketing asset:', error);
    throw new Error(error.message || 'Failed to upload marketing asset');
  }
};

export const listMarketingAssets = async (userId: string, campaignId?: string) => {
  const folderPath = campaignId ? `${userId}/${campaignId}` : `${userId}`;
  
  return supabase.storage
    .from('marketing')
    .list(folderPath);
};

export const deleteStorageFile = async (bucket: 'logos' | 'marketing', filePath: string) => {
  return supabase.storage
    .from(bucket)
    .remove([filePath]);
};

// Helper function to ensure a user record exists in the public.users table
export const ensureUserExists = async (userId: string, userData: any = {}, bypassRls = false) => {
  try {
    // Always use service role client to bypass RLS
    const client = createServiceRoleClient();
    
    // Check if the user exists
    const { data: existingUser, error: checkError } = await client
      .from('users')
      .select('id')
      .eq('id', userId)
      .maybeSingle();
    
    if (checkError) {
      console.error('Error checking if user exists:', checkError);
      return { error: checkError };
    }
    
    // If user exists, return early
    if (existingUser) {
      return { data: existingUser };
    }
    
    // At this point, we know the user doesn't exist and we need to create one
    
    // Get user details from auth.users if available
    const { data: authUser, error: authError } = await client.auth.admin.getUserById(userId);
    
    if (authError) {
      console.error('Error fetching auth user:', authError);
    }
    
    // Get the user's email from auth session if possible, otherwise use provided data
    let email = userData.email || (authUser?.user?.email);
    
    if (!email) {
      // Try to get the current user's email from session
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData?.session?.user?.email) {
        email = sessionData.session.user.email;
      }
    }
    
    // Generate full name from auth user metadata or from provided data
    const fullName = userData.full_name || 
      (authUser?.user?.user_metadata?.full_name) ||
      (userData.first_name || userData.last_name ? 
        `${userData.first_name || ''} ${userData.last_name || ''}`.trim() : 
        (email ? email.split('@')[0] : 'New User'));
    
    // Create the user object
    const newUserData = {
      id: userId,
      email: email || 'user@example.com', // Fallback email
      full_name: fullName,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      role: 'customer', // Using 'customer' as default role
      is_active: true,
    };
    
    console.log('Creating new user in public.users table:', newUserData);
    
    // Insert using the service role client to bypass RLS
    const { data: newUser, error: insertError } = await client
      .from('users')
      .insert(newUserData)
      .select()
      .single();
    
    if (insertError) {
      console.error('Error creating user:', insertError);
      return { error: insertError };
    }
    
    console.log('User created successfully:', newUser);
    return { data: newUser };
  } catch (error) {
    console.error('Error in ensureUserExists:', error);
    return { error };
  }
};

// Helper function to create a restaurant, bypassing RLS if needed
export const createRestaurant = async (restaurantData: any, bypassRls = false) => {
  try {
    // Use the appropriate client based on whether we want to bypass RLS
    const client = bypassRls ? createServiceRoleClient() : supabase;
    
    // First ensure the user exists in the database
    const userResult = await ensureUserExists(restaurantData.owner_id, {}, bypassRls);
    
    if (userResult.error) {
      console.error('Error ensuring user exists:', userResult.error);
      return { error: userResult.error };
    }
    
    // Insert the restaurant
    const { data, error } = await client
      .from('restaurants')
      .insert(restaurantData)
      .select()
      .single();
    
    if (error) throw error;
    
    // If we successfully created the restaurant, also create the waitlist_settings
    if (data && data.id) {
      const waitlistSettings = {
        restaurant_id: data.id,
        auto_sms_enabled: true,
        sms_when_added: true,
        sms_when_ready: true,
        default_estimated_wait_time: 15,
      };
      
      const { error: settingsError } = await client
        .from('waitlist_settings')
        .insert(waitlistSettings);
      
      if (settingsError) {
        console.error('Error creating waitlist settings:', settingsError);
      }
    }
    
    return { data };
  } catch (error: any) {
    console.error('Error creating restaurant:', error);
    return { error };
  }
};

// Add a function to register a restaurant with admin privileges
export const adminRegisterRestaurant = async (userId: string, userEmail: string, restaurantData: any) => {
  try {
    if (!supabaseServiceRoleKey) {
      console.error('Cannot perform admin operation: No service role key available');
      return { error: new Error('Service role key not configured') };
    }
    
    console.log('Creating admin client');
    // Create a direct admin client with the service role key
    const adminClient = createClient(
      finalSupabaseUrl,
      supabaseServiceRoleKey,
      {
        auth: {
          persistSession: false,
          autoRefreshToken: false,
        }
      }
    );
    
    console.log('Creating user record');
    // First create/update the user record
    const { error: userError } = await adminClient
      .from('users')
      .upsert({
        id: userId,
        email: userEmail,
        full_name: restaurantData.name + ' Owner',
        role: 'customer',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_active: true
      });
    
    if (userError) {
      console.error('Error creating user record:', userError);
      return { error: userError };
    }
    
    console.log('Creating restaurant record');
    // Then create the restaurant
    const { data: restaurant, error: restaurantError } = await adminClient
      .from('restaurants')
      .insert({
        owner_id: userId,
        name: restaurantData.name,
        description: restaurantData.description || '',
        address: restaurantData.address,
        city: restaurantData.city,
        state: restaurantData.state,
        zip_code: restaurantData.zip_code,
        phone: restaurantData.phone,
        email: restaurantData.email,
        website: restaurantData.website || '',
        logo_url: restaurantData.logo_url,
        opening_hours: restaurantData.opening_hours,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        is_active: true
      })
      .select()
      .single();
    
    if (restaurantError) {
      console.error('Error creating restaurant:', restaurantError);
      return { error: restaurantError };
    }
    
    console.log('Creating waitlist settings');
    // Finally create the waitlist settings
    if (restaurant) {
      const { error: settingsError } = await adminClient
        .from('waitlist_settings')
        .insert({
          restaurant_id: restaurant.id,
          auto_sms_enabled: true,
          sms_when_added: true,
          sms_when_ready: true,
          default_estimated_wait_time: 15,
        });
      
      if (settingsError) {
        console.error('Error creating waitlist settings:', settingsError);
        // Continue anyway as the restaurant was created
      }
    }
    
    return { data: restaurant };
  } catch (error: any) {
    console.error('Admin restaurant registration error:', error);
    return { error };
  }
};

// Import all functions from the src/supabase.ts file
export * from './src/supabase'; 